// Chemin: C:\gestion-copro\src\components\ui\AideContextuelleModule.tsx
// Session 136 : Composant réutilisable — Aide contextuelle 2 niveaux visuels
// Niveau 1 : Accueil riche (3 colonnes : pourquoi / comment / SENTINELLE)
// Niveau 2 : Bandeau compact permanent avec bouton "En savoir plus"
// Utilisé par toutes les pages VIGIE ayant une aide contextuelle

import React from 'react';
import {
  X,
  CheckCircle,
  Sparkles,
  BookOpen,
} from 'lucide-react';

// ============================================
// TYPES
// ============================================

export interface AideColonne {
  /** Titre de la colonne (ex: "Pourquoi ?") */
  titre: string;
  /** Contenu JSX de la colonne */
  contenu: React.ReactNode;
}

export interface AideContextuelleConfig {
  /** Titre principal de l'accueil riche (ex: "Bienvenue dans tes correspondances") */
  titreAccueil: string;
  /** Sous-titre de l'accueil riche */
  sousTitre: string;
  /** 3 colonnes de l'accueil riche */
  colonnes: [AideColonne, AideColonne, AideColonne];
  /** Icône lucide-react pour le bandeau compact */
  iconeCompact: React.ReactNode;
  /** Texte du bandeau compact (HTML autorisé via dangerouslySetInnerHTML ou JSX) */
  texteCompact: React.ReactNode;
}

interface AideContextuelleModuleProps {
  config: AideContextuelleConfig;
  /** true si l'accueil riche a déjà été vu */
  aideAccueilVue: boolean;
  /** true si réouverture depuis le compact */
  afficherAideComplete: boolean;
  /** Callback fermeture accueil riche */
  onFermer: () => void;
  /** Callback "En savoir plus" depuis compact */
  onVoirPlus: () => void;
}

// ============================================
// COMPOSANT : ACCUEIL RICHE (Niveau 1)
// ============================================

const AideAccueil: React.FC<{
  config: AideContextuelleConfig;
  onFermer: () => void;
}> = ({ config, onFermer }) => {
  return (
    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl overflow-hidden">
      {/* En-tête */}
      <div className="p-5 pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-blue-100 rounded-xl">
              <Sparkles className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">{config.titreAccueil}</h2>
              <p className="text-sm text-blue-700">{config.sousTitre}</p>
            </div>
          </div>
          <button
            onClick={onFermer}
            className="p-1.5 rounded-lg hover:bg-blue-100 transition-colors flex-shrink-0"
            title="J'ai compris"
          >
            <X className="w-4 h-4 text-blue-400" />
          </button>
        </div>
      </div>

      {/* Contenu — 3 colonnes */}
      <div className="px-5 pb-5">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {config.colonnes.map((col, idx) => (
            <div key={idx} className="bg-white/70 rounded-lg p-4">
              <p className="text-xs font-bold text-blue-600 uppercase tracking-wide mb-2">{col.titre}</p>
              <div className="text-sm text-gray-700">{col.contenu}</div>
            </div>
          ))}
        </div>

        {/* Bouton fermer */}
        <div className="mt-4 text-center">
          <button
            onClick={onFermer}
            className="inline-flex items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors"
          >
            <CheckCircle className="w-4 h-4" />
            J'ai compris, c'est parti
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================
// COMPOSANT : BANDEAU COMPACT (Niveau 2)
// ============================================

const AideCompacte: React.FC<{
  config: AideContextuelleConfig;
  onVoirPlus: () => void;
}> = ({ config, onVoirPlus }) => {
  return (
    <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 flex items-center gap-3">
      <div className="p-1.5 bg-blue-100 rounded-lg flex-shrink-0">
        {config.iconeCompact}
      </div>
      <div className="text-sm text-blue-800 flex-1">
        {config.texteCompact}
      </div>
      <button
        onClick={onVoirPlus}
        className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-100 rounded-lg transition-colors flex-shrink-0"
      >
        <BookOpen className="w-3.5 h-3.5" />
        En savoir plus
      </button>
    </div>
  );
};

// ============================================
// COMPOSANT PRINCIPAL EXPORTÉ
// ============================================

const AideContextuelleModule: React.FC<AideContextuelleModuleProps> = ({
  config,
  aideAccueilVue,
  afficherAideComplete,
  onFermer,
  onVoirPlus,
}) => {
  if (!aideAccueilVue || afficherAideComplete) {
    return <AideAccueil config={config} onFermer={onFermer} />;
  }
  return <AideCompacte config={config} onVoirPlus={onVoirPlus} />;
};

export default AideContextuelleModule;
